import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import { Grid, Paper, Switch, Tab, Tabs } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'

import { useIsINSEnabled } from '@/hooks/useIsINSEnabled'
import { useIsWebView } from '@/hooks/useSizeBreakpoints'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { setAppSettings } from '@/redux/reducers/app.reducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'

import CalendsoPassword from '../containers/CalendsoPassword'
import ChangePasswordDialog from '../containers/ChangePasswordDialog'
import CustomDevices from '../containers/CustomDevices'
import CustomDeviceForm from '../containers/CustomDeviceForm'
import DisplayChatForm from '../components/DisplayChatForm'
import ProfileForm from '../containers/ProfileForm'
import WhatsAppBindingsPanel from '../components/WhatsAppBindingsPanel'
import ConclusionSentencesForm from '../containers/ConclusionSentencesForm'
import SelectTimeSlot from '../containers/SelectTimeSlot'
import Payment from '../containers/Payment'
import MedconnectDevicesConfigurator from '../containers/MedconnectDeviceConfigurator'
import { PatientManagement } from '../components/PatientManagement'

import { ConfigurationTabPanel } from './ConfigurationTabPanel'
import { TabValue, useConfigurationTabs } from '../model/useConfigurationTabs'
import { Appearance } from './Appearance'
import { TimeTracking } from './TimeTracking'
import { UnitSystem } from './UnitSystem'

type ConfigurationPageProps = {}

const goToAppDevice = () => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage('deviceConfig')
  } else {
    console.error('ReactNativeWebView is not defined')
  }
}

export function ConfigurationPage(props: ConfigurationPageProps) {
  useLingui()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const i18next = useTranslation()

  const authUser = useAppSelector(selectAuthUser)
  const appSettings = useAppSelector((state) => state.app.settings)

  const isINSEnabled = useIsINSEnabled()
  const isWebView = useIsWebView()

  const tabs = useConfigurationTabs()

  const [tabValue, setTabValue] = useState<TabValue>(() => {
    const tab = searchParams.get('tab') as TabValue | undefined
    return tab ?? 'profile'
  })

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const section = queryParams.get('section')

    if (section) {
      const element = document.getElementById(section)

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [location])

  if (!authUser) return null

  const isDoctor = isStaff(authUser) && authUser.status === 'MEDECIN'

  return (
    <Container maxWidth="xl">
      <Box sx={{ px: !isWebView ? '36px' : '10px', pt: !isWebView ? '36px' : '24px', pb: !isWebView ? '36px' : '24px' }}>
        <Stack spacing={4}>
          <Stack direction="row" justifyContent="right" alignContent="center" spacing={1.5}>
            <ChangePasswordDialog />
            <Button component={Link} to="/account-verification" color="info" disableElevation variant="contained">
              <Trans>Getting started</Trans>
            </Button>
          </Stack>
          <Paper>
            <Grid container>
              <Grid item xs={12} md={3}>
                <Tabs
                  orientation="vertical"
                  onChange={(event, value) => {
                    searchParams.set('tab', value)
                    setSearchParams(searchParams.toString())
                    setTabValue(value as TabValue)
                  }}
                  value={tabValue}
                  sx={{ py: 1, '& .MuiTab-root': { alignItems: 'start', maxWidth: 'none', textAlign: 'left', textTransform: 'initial' } }}
                >
                  {tabs.map((tab) => (
                    <Tab key={tab.value} label={tab.label} value={tab.value} />
                  ))}
                </Tabs>
              </Grid>
              <Grid item xs={12} md={9}>
                <Box sx={{ height: '100%', borderLeft: 1, borderColor: 'divider' }}>
                  <ConfigurationTabPanel title={t`Personal informations`} subtitle={t`Your personnal information`} value={tabValue} index="profile">
                    <Stack spacing={4}>
                      <ProfileForm />
                      <WhatsAppBindingsPanel />
                    </Stack>
                  </ConfigurationTabPanel>
                  <ConfigurationTabPanel title={t`Appearance`} subtitle={t`Customize the display of elements`} value={tabValue} index="appearance">
                    <Appearance />
                  </ConfigurationTabPanel>
                  {isDoctor && (
                    <ConfigurationTabPanel title={t`Report type`} subtitle={t`Predefined text`} value={tabValue} index="conclusion-sentences">
                      <ConclusionSentencesForm />
                    </ConfigurationTabPanel>
                  )}
                  <ConfigurationTabPanel title={t`Device setting`} value={tabValue} index="custom-devices">
                    {!isWebView ? (
                      <Stack spacing={2}>
                        <CustomDeviceForm />
                        <CustomDevices />
                      </Stack>
                    ) : (
                      <Button onClick={goToAppDevice} color="primary" disableElevation variant="contained">
                        <Trans>Devices setting</Trans>
                      </Button>
                    )}
                  </ConfigurationTabPanel>
                  {!isWebView && (
                    <ConfigurationTabPanel title={t`Local devices settings`} subtitle={t`Settings informations`} value={tabValue} index="medconnect-devices">
                      <Button
                        variant="contained"
                        color="primary"
                        href={'https://medconnectinstallation.s3.eu-west-3.amazonaws.com/medconnect-devices/medconnect-devices.exe'}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mb: 2 }}
                      >
                        <Trans>Download the last version of the Medconnect program</Trans>
                      </Button>
                      <MedconnectDevicesConfigurator />
                    </ConfigurationTabPanel>
                  )}
                  {isINSEnabled && (
                    <ConfigurationTabPanel title={t`Patient management`} value={tabValue} index="patient-management">
                      <PatientManagement />
                    </ConfigurationTabPanel>
                  )}
                  <ConfigurationTabPanel title={t`Payment`} subtitle={t`Payment configuration`} value={tabValue} index="payment">
                    <Payment />
                  </ConfigurationTabPanel>
                  <ConfigurationTabPanel
                    title={t`My availabilities`}
                    subtitle={t`Appointments slots you want to share with your patients`}
                    value={tabValue}
                    index="select-time-slot"
                  >
                    <SelectTimeSlot />
                  </ConfigurationTabPanel>
                  <ConfigurationTabPanel
                    title={t`Chat settings`}
                    subtitle={t`Show chatroom canal inside MedConnect`}
                    value={tabValue}
                    index="display-chat-form"
                  >
                    <DisplayChatForm />
                  </ConfigurationTabPanel>
                  <ConfigurationTabPanel title={t`Your calcom credentials`} subtitle={t`Your calendso password`} value={tabValue} index="calendso-password">
                    <CalendsoPassword />
                  </ConfigurationTabPanel>
                  <ConfigurationTabPanel
                    index="autoSwitchOffline"
                    title={i18next.t('page.configuration.autoSwitchOffline.title')}
                    subtitle={i18next.t('page.configuration.autoSwitchOffline.subtitle')}
                    value={tabValue}
                  >
                    <Switch
                      checked={appSettings.autoSwitchOfflineMode}
                      onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                        dispatch(setAppSettings({ autoSwitchOfflineMode: checked }))
                        localStorage.setItem('autoSwitchOfflineMode', `${checked}`)
                      }}
                    />
                  </ConfigurationTabPanel>
                  <ConfigurationTabPanel title={t`Time tracking`} subtitle={t`Enable or disable working hours tracking`} value={tabValue} index="time-tracking">
                    <TimeTracking />
                  </ConfigurationTabPanel>
                  <ConfigurationTabPanel title={t`Unit system`} value={tabValue} index="unit-system">
                    <UnitSystem />
                  </ConfigurationTabPanel>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Stack>
      </Box>
    </Container>
  )
}
